import { motion } from "framer-motion";
import { Container } from "@/components/layout/Container";
import { Greeting } from "@/components/dashboard/Greeting";
import { ResumeCard } from "@/components/dashboard/ResumeCard";
import { TestAssignmentCard } from "@/components/dashboard/TestAssignmentCard";
import { NoticeStack } from "@/components/dashboard/NoticeStack";
import { GoalWidget } from "@/components/dashboard/GoalWidget";
import { ThisWeekStrip } from "@/components/dashboard/ThisWeekStrip";
import { StudyTimeChart } from "@/components/dashboard/StudyTimeChart";
import { DailyQuestionCard } from "@/components/dashboard/DailyQuestionCard";
import { SubjectProgress } from "@/components/dashboard/SubjectProgress";
import { QuizPerformance } from "@/components/dashboard/QuizPerformance";
import { RecentChapters } from "@/components/dashboard/RecentChapters";
import { AITutorMiniPrompt } from "@/components/dashboard/AITutorMiniPrompt";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDashboard, useResume, useStreak } from "@/hooks/useProgress";
import { firstName } from "@/lib/format";

/**
 * The student dashboard. Phase 8e.
 *
 * Composition (mobile-first, single column; lg+ splits 2-up):
 *   1. Hero band -- greeting + streak chip (signature flourish)
 *   2. Resume card -- "Continue where you left off" (one-tap deep link)
 *   3. This week -- 3 tabular stat tiles
 *   4. Study time -- big-number card with smooth curve underneath
 *   5. Daily question -- locked daily MCQ + practice mode
 *   6. Recent chapters -- horizontal scroll
 *   7. Subjects -- per-subject progress bars
 *   8. Quiz performance -- best / average / last + sparkline
 *   9. AI Tutor mini-prompt -- opens the sheet pinned to most-recent chapter
 *
 * All data sources already wired (useDashboard, useResume, useStreak,
 * useCurrentUser). The dashboard self-handles its loading / empty
 * states without requiring a top-level skeleton.
 */
export function DashboardPage() {
  const me = useCurrentUser();
  const dashboard = useDashboard();
  const resume = useResume();
  const streak = useStreak();

  const fn = firstName(me.data?.user_name);
  const userId = me.data?.user_id ?? null;
  const classId = me.data?.class_id ?? null;
  const studentName = me.data?.user_name ?? null;

  const last7 = dashboard.data?.last_7_days;
  const chatMessages = last7?.total_chat_messages ?? 0;
  const quizAttempts = last7?.total_quiz_attempts ?? 0;
  const daily = last7?.daily_seconds ?? [];
  const dailyChatMessages = last7?.daily_chat_messages;
  const dailyQuizAttempts = last7?.daily_quiz_attempts;

  // Avg quiz score this week, derived client-side from recent_chapters.
  // The dashboard endpoint doesn't return per-attempt history -- we only
  // have each chapter's most-recent score + timestamp. So we average the
  // `quiz_last_score` across chapters whose `quiz_last_attempted_at` is
  // inside the last-7-days window. Approximation, but the right shape.
  const { avgQuizScore, quizScoreValues } = (() => {
    const recent = dashboard.data?.recent_chapters ?? [];
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const scores = recent
      .filter(
        (p) =>
          typeof p.quiz_last_score === "number" &&
          p.quiz_last_attempted_at &&
          new Date(p.quiz_last_attempted_at).getTime() >= cutoff,
      )
      .sort((a, b) => new Date(a.quiz_last_attempted_at!).getTime() - new Date(b.quiz_last_attempted_at!).getTime())
      .map((p) => p.quiz_last_score!);
    const avg = scores.length === 0 ? 0 : scores.reduce((a, b) => a + b, 0) / scores.length;
    return { avgQuizScore: avg, quizScoreValues: scores };
  })();

  // Last activity timestamp powers the "returning after a gap" greeting.
  const lastSeenIso = resume.data?.last_accessed_at ?? null;
  const isBrandNew = !resume.data && (dashboard.data?.recent_chapters?.length ?? 0) === 0;

  return (
    <Container size="lg" className="pt-3 sm:pt-4 lg:pt-5 pb-5 sm:pb-8 lg:pb-12">
      <Greeting
        name={studentName}
        userId={userId}
        streakDays={streak.data?.current_streak_days ?? 0}
        longestStreak={streak.data?.longest_streak_days_60d ?? 0}
        lastSeenIso={lastSeenIso}
        isBrandNew={isBrandNew}
      />

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.32, delay: 0.04, ease: [0.16, 1, 0.3, 1] }}
        className="mt-4 sm:mt-5 lg:mt-5 grid gap-4 sm:gap-5 lg:gap-6 lg:grid-cols-3"
      >
        {/* Left column (2/3 on lg+) -- the consumed-on-arrival stack.
            Order: resume -> this-week stats -> study-time graph ->
            daily check-in. min-w-0 lets flex children honour parent
            width so no descendant pushes the grid item wider than the
            viewport on tight mobiles. */}
        <div className="min-w-0 lg:col-span-2 flex flex-col gap-4 sm:gap-5 lg:gap-6">
          <NoticeStack />
          <TestAssignmentCard />
          <GoalWidget />
          <ResumeCard resume={resume.data ?? null} firstName={fn} />
          <ThisWeekStrip
            avgQuizScore={avgQuizScore}
            quizScoreValues={quizScoreValues}
            chatMessages={chatMessages}
            quizAttempts={quizAttempts}
            dailyChatMessages={dailyChatMessages}
            dailyQuizAttempts={dailyQuizAttempts}
          />
          <StudyTimeChart daily={daily} />
          <DailyQuestionCard userId={userId} classId={classId} studentName={studentName} />
        </div>

        {/* Right column (1/3 on lg+) -- the discovery stack. */}
        <div className="min-w-0 flex flex-col gap-4 sm:gap-5 lg:gap-6">
          <RecentChapters classId={classId} />
          <SubjectProgress classId={classId} />
          <QuizPerformance />
          <AITutorMiniPrompt
            pinnedChapterId={resume.data?.chapter_id ?? null}
            firstName={fn}
          />
        </div>
      </motion.div>
    </Container>
  );
}
